/**
 * Single source of truth for classifying a user's questions as answered / unanswered / incorrect
 * per topic. Shared between web and mobile applications.
 *
 * Both the readiness dashboard (which needs COUNTS) and the custom-test builder (which needs the
 * actual question POOLS) call this so the two can never disagree again.
 *
 * Two rules make it robust:
 *  1. Bundle-anchored — a question's topic and existence are defined by the bundled `questions`
 *     list, NOT by the score record. We iterate the bundle and look up each question's record, so
 *     "answered" records for questions that were retired/renumbered out of the bundle (orphans) are
 *     ignored instead of inflating the answered count.
 *  2. Type-insensitive — score records persist `topic_id` (and historically `question_id`) with
 *     mixed string|number types. All id comparisons normalize to String, matching the loose `==`
 *     the dashboard has always relied on. A strict `includes`/`===` silently drops the string-typed
 *     records and lets already-answered questions leak back into the "unanswered" pool.
 *
 * @param {Array}  questions  bundled exam questions: { id, topic_id, ... }
 * @param {Array}  scores     per-question score records: { question_id, topic_id, times_answered, score, accuracy_score }
 * @param {Array}  topicIds   topic/domain ids to include
 * @param {Object} [opts]     { bookmarkIds?: Array }
 * @returns {{ total:number, answeredCount:number, correctCount:number, incorrectCount:number,
 *            accuracySum:number, unanswered:Array, incorrect:Array, bookmarked:Array, attempted:Array }}
 */
export function computeQuestionPools(questions, scores, topicIds, opts = {}) {
  const topicSet = new Set((topicIds ?? []).map((t) => String(t)));
  const bookmarkSet = new Set((opts.bookmarkIds ?? []).map((b) => String(b)));

  // A question only counts as answered once it has actually been answered (times_answered >= 1).
  // Stub records (times_answered === 0, created by viewing/skipping/bookmarking) are not answers.
  const answeredById = new Map();
  for (const s of scores ?? []) {
    if ((s?.times_answered ?? 0) >= 1) answeredById.set(String(s.question_id), s);
  }

  const unanswered = [];
  const incorrect = [];
  const bookmarked = [];
  const attempted = [];
  let correctCount = 0;
  let accuracySum = 0;

  for (const q of questions ?? []) {
    if (!topicSet.has(String(q.topic_id))) continue;
    const id = String(q.id);
    const rec = answeredById.get(id);
    if (rec) {
      attempted.push(q);
      accuracySum += rec.accuracy_score ?? 0;
      if ((rec.score ?? 0) >= 1) correctCount += 1;
      else incorrect.push(q);
    } else {
      unanswered.push(q);
    }
    if (bookmarkSet.has(id)) bookmarked.push(q);
  }

  return {
    total: attempted.length + unanswered.length,
    answeredCount: attempted.length,
    correctCount,
    incorrectCount: attempted.length - correctCount,
    accuracySum,
    unanswered,
    incorrect,
    bookmarked,
    attempted,
  };
}
